export const tabs = {
  selector: '.v-component--tabs',
  reactive: true,

  run(el, data, root) {
    const tabList = el.querySelector('[role="tablist"]');
    const manual = el.hasAttribute('data-manual');
    const scrollable = el.hasAttribute('data-scrollable');
    const storageKey = `tabs-active-${el.id || 'tabs-group'}`;

    const getTabs = () => Array.from(el.querySelectorAll('[role="tab"]'));
    const getPanels = () => Array.from(el.querySelectorAll('[role="tabpanel"]'));

    function setupTabs() {
      const tabs = getTabs();
      const panels = getPanels();

      tabs.forEach((tab, index) => {
        const tabId = tab.id || `tab-${index}`;
        const panelId = tab.getAttribute('aria-controls') || `tabpanel-${index}`;
        const panel = el.querySelector(`#${panelId}`);

        tab.id = tabId;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panelId);
        tab.setAttribute('tabindex', '-1'); // inactive by default
        tab.setAttribute('aria-selected', 'false');

        if (panel) {
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tabId);
          panel.setAttribute('hidden', '');
          panel.id = panelId;
        }

        // Один раз на элемент: setupTabs() вызывается повторно из
        // MutationObserver, и без этой отметки на каждой перерисовке
        // содержимого панелей копился ещё один обработчик клика.
        if (!tab.__tabsBound) {
          tab.addEventListener('click', () => activateTab(tab));
          tab.__tabsBound = true;
        }
      });
    }

    function activateTab(tab, focus = true) {
      const tabs = getTabs();
      const panels = getPanels();

      tabs.forEach(t => {
        t.setAttribute('aria-selected', 'false');
        t.setAttribute('tabindex', '-1');
      });

      panels.forEach(p => {
        p.setAttribute('hidden', '');
      });

      tab.setAttribute('aria-selected', 'true');
      tab.setAttribute('tabindex', '0');
      if (focus) tab.focus();

      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) {
        panel.removeAttribute('hidden');
      }

      // Persist only for user-driven activations (focus=true). Silent
      // activations (e.g. scroll-sync from a drag/scroll handler) skip
      // persistence to avoid flooding localStorage with intermediate tabs.
      if (focus) {
        localStorage.setItem(storageKey, tab.id);
      }
    }

    function activateById(tabId, focus = true) {
      const tab = el.querySelector(`#${tabId}`);
      if (tab && tab.getAttribute('role') === 'tab') {
        activateTab(tab, focus);
      }
    }

    function getActiveTab() {
      return el.querySelector('[role="tab"][aria-selected="true"]');
    }

    function handleKey(e) {
      const tabs = getTabs();
      const current = document.activeElement;
      const index = tabs.indexOf(current);
      if (index === -1) return;

      let nextIndex = index;
      const isHorizontal = tabList.getAttribute('aria-orientation') !== 'vertical';

      switch (e.key) {
        case 'ArrowRight':
          if (isHorizontal) nextIndex = (index + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          if (isHorizontal) nextIndex = (index - 1 + tabs.length) % tabs.length;
          break;
        case 'ArrowDown':
          if (!isHorizontal) nextIndex = (index + 1) % tabs.length;
          break;
        case 'ArrowUp':
          if (!isHorizontal) nextIndex = (index - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        case 'Enter':
        case ' ':
          if (manual) {
            activateTab(tabs[index]);
            e.preventDefault();
          }
          return;
        default:
          return;
      }

      e.preventDefault();
      tabs[nextIndex].focus();
      if (!manual) activateTab(tabs[nextIndex]);
    }

    function activateFromHash() {
      const hash = window.location.hash.substring(1);
      if (!hash) return;

      const tab = el.querySelector(`[aria-controls="${hash}"]`);
      // Initial hash activation must NOT focus the tab — focus() would
      // scroll it into view, jumping the page (especially bad when the
      // tabs are in the footer).
      if (tab) activateTab(tab, false);
    }

    function activateFromStorage() {
      const savedId = localStorage.getItem(storageKey);
      if (savedId) activateById(savedId, false);
    }

    function bindShortcuts() {
      document.addEventListener('keydown', e => {
        if (e.altKey && /^[1-9]$/.test(e.key)) {
          const index = parseInt(e.key, 10) - 1;
          const tab = getTabs()[index];
          if (tab) {
            e.preventDefault();
            activateTab(tab);
          }
        }
      });
    }

    // Initial setup
    setupTabs();

    const hash = window.location.hash.substring(1);
    const savedId = localStorage.getItem(storageKey);
    const tabs = getTabs();
    const firstTab = tabs[0];

    // All init paths use focus=false — focusing a tab on page load makes
    // the browser scroll it into view, which jumps the viewport down to
    // the tabs (catastrophic when tabs live in the footer).
    if (hash) {
      activateFromHash();
    } else if (savedId && el.querySelector(`#${savedId}`)) {
      activateById(savedId, false);
    } else if (firstTab) {
      activateTab(firstTab, false);
    }

    tabList.addEventListener('keydown', handleKey);
    window.addEventListener('hashchange', activateFromHash);

    // Пересборка при изменении содержимого — но с восстановлением активной
    // вкладки: setupTabs() снимает выделение со всех вкладок и прячет все
    // панели, поэтому без этого любая перерисовка внутри панели (например,
    // подстановка строк заказа) оставляла компонент вообще без открытой
    // вкладки. Наблюдаем только childList, атрибуты не трогаем — иначе
    // activateTab() запускал бы наблюдатель заново.
    new MutationObserver(() => {
      const activeId = (getActiveTab() || {}).id;
      setupTabs();
      if (activeId) activateById(activeId, false);
      else if (getTabs()[0]) activateTab(getTabs()[0], false);
    }).observe(el, { childList: true, subtree: true });
    bindShortcuts();

    // Public API
    el.tabs = {
      activate: (tabId, opts) => activateById(tabId, !opts || opts.focus !== false),
      next() {
        const tabs = getTabs();
        const i = tabs.indexOf(getActiveTab());
        activateTab(tabs[(i + 1) % tabs.length]);
      },
      prev() {
        const tabs = getTabs();
        const i = tabs.indexOf(getActiveTab());
        activateTab(tabs[(i - 1 + tabs.length) % tabs.length]);
      },
      getActive: getActiveTab
    };
  }
};
